import { Fieldset, Heading, HStack, Spacer, Stack } from '@chakra-ui/react'
import isNull from 'lodash/isNull'
import omitBy from 'lodash/omitBy'
import type { FC } from 'react'
import { useMemo } from 'react'
import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'

import MapsSectionContent from '@app/pages/maps/components/mapsSectionContent'
import AiPlaybooksModal from '@app/pages/playbooks/aiPlaybooksModal'
import AiPlaybooksModalTrigger from '@app/pages/playbooks/components/aiPlaybooksModalTrigger'
import { useListFilterContext } from '@app/pages/shared/listFilterContext'
import Can from '@app/shared/authorization/can'
import extractOrderings from '@app/shared/utils/extractOrderings'
import withSuspenseWrapper from '@app/shared/withSuspenseWrapper'
import { useStore } from '@app/store'
import type { DomainBusinessContext } from '@app/types'
import { Strategies } from '@graphql/documents/strategy.graphql'
import type { StrategiesQuery, StrategiesQueryVariables } from '@graphql/queries'
import { usePlaybooksQuery } from '@graphql/queries'

interface Props {
  businessContext?: DomainBusinessContext | null
  loading?: boolean
}

const MapsSection: FC<Props> = ({ businessContext = null }) => {
  // This refreshToken is used to trigger a re-fetch of the data when the business context id changes. It's hopefully
  // a temporary solution until we get rid of the Maps/Context Graph split and can simplify the data fetching logic.
  const { refreshToken, listFilter: filter } = useListFilterContext()

  const [{ data }] = usePlaybooksQuery({ variables: { aiOnly: true } })
  const addObjectPage = useStore.use.addObjectPage()
  const loaderQuery = useStore.use.loaderQuery()

  const [searchParams] = useSearchParams()
  const page = searchParams.get('page')
  const limit = searchParams.get('limit')
  const order = useMemo(() => extractOrderings(searchParams), [searchParams])

  useEffect(() => {
    async function fetchData() {
      const variables: StrategiesQueryVariables = omitBy(
        { page, limit, filter, order, businessContextId: businessContext?.id },
        isNull
      )

      const result = await loaderQuery<StrategiesQuery, StrategiesQueryVariables>(Strategies, variables)
      const { collection: c = [], metadata: m } = result.data.strategies
      addObjectPage('strategy', c, m)
    }

    fetchData()
  }, [addObjectPage, businessContext?.id, filter, limit, loaderQuery, order, page, refreshToken])

  return (
    <Stack gap={2}>
      <HStack>
        <Heading size="md">Maps</Heading> <Spacer />
        <Can I="create" a="strategy">
          <AiPlaybooksModal playbooks={data?.playbooks} showCTA={true} businessContextId={businessContext?.id}>
            <AiPlaybooksModalTrigger cta="Build map" size="xs" />
          </AiPlaybooksModal>
        </Can>
      </HStack>
      <Fieldset.Root bg="bg" borderWidth={1} borderStyle="solid" borderColor="border.default" borderRadius="xs" p={3}>
        <Fieldset.Content>
          <MapsSectionContent />
        </Fieldset.Content>
      </Fieldset.Root>
    </Stack>
  )
}

export default withSuspenseWrapper(MapsSection)
